import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';
import {
  runSimulation as runSimulationService,
  deleteSimulation as deleteSimulationService,
} from '../services/api';
import type { Simulation, SimulationInput } from '../types';

/**
 * Hook to fetch simulations for a project
 * Uses real-time Firestore listener
 */
export const useSimulations = (projectId: string | null) => {
  const { user } = useAuth();
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setSimulations([]);
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'simulations'),
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const sims = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              simulationId: doc.id,
              projectId: data.projectId,
              title: data.title,
              topic: data.topic,
              format: data.format,
              personaIds: data.personaIds,
              transcriptUrl: data.transcriptUrl,
              transcriptMd: data.transcriptMd,
              status: data.status,
              createdAt: data.createdAt?.toDate() || new Date(),
              completedAt: data.completedAt?.toDate(),
            } as Simulation;
          });

          setSimulations(sims);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching simulations:', err);
          setError(err as Error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up simulations listener:', err);
      setError(err as Error);
      setLoading(false);
    }
  }, [user, projectId]);

  return { simulations, loading, error };
};

/**
 * Hook to run a simulation
 */
export const useRunSimulation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const runSimulation = useCallback(async (input: SimulationInput) => {
    setLoading(true);
    setError(null);

    try {
      const result = await runSimulationService(input);
      setLoading(false);
      return result;
    } catch (err) {
      console.error('Error running simulation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to run simulation';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { runSimulation, loading, error };
};

/**
 * Hook to delete a simulation
 */
export const useDeleteSimulation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteSimulation = useCallback(async (simulationId: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteSimulationService(simulationId);
      setLoading(false);
    } catch (err) {
      console.error('Error deleting simulation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete simulation';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { deleteSimulation, loading, error };
};
